import {
  Flexbox, LoadManager, PageHeader, Styles,
} from '@/components'
import { CLINICAL_CARE_EVENT_STATUS, CLINICAL_CARE_STATUS } from '@/lib/enums'
import useAxiosRequest from '@/hooks/useAxiosRequest'
import usePageTitle from '@/hooks/usePageTitle'
import clinicalCares from '@/services/api/clinicalCares'
import React from 'react'
import { useParams } from 'react-router-dom'
import dateMask from '@/lib/masks/date'
import currency from '@/lib/masks/currency'
import { clinicalCareStatusClasses } from '@/constants/clinicalCares'
import PersonCard from '../components/PersonCard'
import AnimalCard from '../components/AnimalCard'
import ClinicalCareCard from '../components/ClinicalCareCard'
import QuestionnaireAnswers from './components/QuestionnaireAnswers'

function ClinicalCareShow({ title }) {
  const { id } = useParams()
  usePageTitle({ title: `${title} #${id}` })
  const { data, loading, error } = useAxiosRequest(clinicalCares.show, id)

  return (
    <Styles.Container className="grid-xl">
      <PageHeader title={`Atendimento clínico #${id}`} icon="fas fa-stethoscope" subtitle={`Código: ${data?.uid || '-'}`} />
      <LoadManager loading={loading} error={error}>
        {
          Boolean(data) && (
            <>
              <div className="mb-2">
                Situação:&nbsp;
                <b className={`${clinicalCareStatusClasses[data.status]}`}>
                  {CLINICAL_CARE_STATUS.t(data.status)}
                </b>
              </div>
              <div className="divider" />
              <Flexbox.Columns>
                <Flexbox.Column className="col-12 mb-2">
                  <h1 className="h5">Detalhes do atendimento</h1>
                </Flexbox.Column>
                <Flexbox.Column className="col-12 col-lg-12 col-sm-12 my-2">
                  <ClinicalCareCard clinicalCare={data} />
                </Flexbox.Column>
              </Flexbox.Columns>
              <div className="divider" />
              <Flexbox.Columns>
                <Flexbox.Column className="col-6 col-lg-6 col-sm-12 my-2">
                  <AnimalCard animal={data?.animal} />
                </Flexbox.Column>
                <Flexbox.Column className="col-6 col-lg-6 col-sm-12 my-2">
                  <PersonCard
                    title="Tutor"
                    icon="fas fa-user"
                    person={data?.tutor}
                    extraData={data?.extraData}
                    message="Nenhum tutor vinculado."
                  />
                </Flexbox.Column>
                <Flexbox.Column className="col-6 col-lg-6 col-sm-12 my-2">
                  <PersonCard
                    title="Solicitante"
                    icon="fas fa-user-md"
                    person={data?.author}
                    message="Nenhum solicitante vinculado."
                  />
                </Flexbox.Column>
                <Flexbox.Column className="col-6 col-lg-6 col-sm-12 my-2">
                  <PersonCard
                    title="Clínica"
                    icon="fas fa-clinic-medical"
                    person={data?.clinic}
                    message="Nenhuma clínica vinculada."
                  />
                </Flexbox.Column>
              </Flexbox.Columns>
              {
                Boolean(data?.items?.length) && (
                  <>
                    <div className="divider" />
                    <h1 className="h5">Itens do atendimento</h1>
                    <table className="table text-small table-hover">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Descrição</th>
                          <th width="100px">Quantidade</th>
                          <th width="120px">Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          data.items.map((item) => (
                            <tr key={item.id}>
                              <td>{item.title}</td>
                              <td>{item.description || '-'}</td>
                              <td>{item.quantity}</td>
                              <td>{currency(item.price || 0)}</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                  </>
                )
              }
              <QuestionnaireAnswers clinicalCare={data} />
              {
                Boolean(data?.events?.length) && (
                  <>
                    <div className="divider" />
                    <h1 className="h5">Histórico</h1>
                    <div className="timeline">
                      {
                        data.events.map((event) => (
                          <div className="timeline-item" key={event.id}>
                            <div className="timeline-left">
                              <span className="timeline-icon icon-lg">
                                <i className={CLINICAL_CARE_EVENT_STATUS.icon(event.status)} />
                              </span>
                            </div>
                            <div className="timeline-content">
                              <div className="tile">
                                <div className="tile-content">
                                  <p className="tile-subtitle">
                                    <span className="text-bold">
                                      {dateMask(event?.createdAt, { dateStyle: 'full' })}&nbsp;
                                    </span>
                                    <small className="label label-sm label-rounded">
                                      <i className="far fa-clock" />&nbsp;
                                      {new Date(event?.createdAt).toLocaleTimeString()}
                                    </small>
                                  </p>
                                  <p className={`tile-title ${CLINICAL_CARE_EVENT_STATUS.class[event.status] || ''}`}>
                                    {CLINICAL_CARE_EVENT_STATUS.t(event.status)}
                                  </p>
                                  {
                                    Boolean(event?.description) && (
                                      <p className="tile-title">
                                        <cite className="pre">{event?.description}</cite>
                                      </p>
                                    )
                                  }
                                  <div className="text-small">{event?.author?.fullName}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </>
                )
              }
            </>
          )
        }
      </LoadManager>
    </Styles.Container>
  )
}

export default ClinicalCareShow
